import { forwardRef } from 'react';
import styled from '@emotion/styled';

import { createChainedFunction } from '@toolbox/utils';
import { useCheckboxGroupContext } from './CheckboxGroupContext';
import { Text } from '../typography';
import { CheckboxError } from './CheckboxError';

import type { InputHTMLAttributes, ReactNode } from 'react';

const CheckboxLabel = styled.label`
  display: inline-flex;
  position: relative;
  align-items: flex-start;
  cursor: pointer;
  text-align: left;

  input {
    border: 0;
    clip: rect(0px, 0px, 0px, 0px);
    height: 1px;
    width: 1px;
    margin: -1px;
    padding: 0;
    overflow: hidden;
    white-space: nowrap;
    position: absolute;
  }
`;

const CheckboxControl = styled.span<{ checked?: boolean; error?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  vertical-align: top;
  flex-shrink: 0;
  font-size: ${({ theme }) => theme.fontSizes.f2};
  margin-top: ${({ theme }) => theme.spacing(1)};
  user-select: none;

  width: 20px;
  height: 20px;
  border: 2px solid
    ${({ theme, error }) => (error ? theme.palette.colors.error : theme.palette.colors.primary)};
  border-radius: 2px;
  color: ${({ theme }) => theme.palette.colors.white};
  margin-right: ${({ theme }) => theme.spacing(3)};

  input:checked ~ & {
    background-color: ${({ theme, error }) =>
      error ? theme.palette.colors.error : theme.palette.colors.primary};
  }
`;

const checkIcon = (
  <svg
    width="1em"
    height="1em"
    viewBox="0 0 12 10"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
    stroke="currentColor"
    strokeWidth="2"
    fill="none"
  >
    <polyline points="1.5 6 4.5 9 10.5 1" />
  </svg>
);

export type CheckboxProps = InputHTMLAttributes<HTMLInputElement> & {
  label?: ReactNode;
  error?: string;
};

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(props, ref) {
  const {
    label,
    checked: checkedProp,
    name: nameProp,
    onChange: onChangeProp,
    error,
    ...inputProps
  } = props;
  const checkboxGroup = useCheckboxGroupContext();

  const checked = checkedProp ?? (checkboxGroup && checkboxGroup.value === props.value);
  const name = nameProp ?? checkboxGroup?.name;

  const handleChange = createChainedFunction(onChangeProp, checkboxGroup?.onChange);
  return (
    <>
      {error && <CheckboxError>{error}</CheckboxError>}
      <CheckboxLabel>
        <input
          {...inputProps}
          type="checkbox"
          ref={ref}
          name={name}
          checked={checked}
          onChange={handleChange}
        />
        <CheckboxControl checked={checked} error={!!error}>
          {checkIcon}
        </CheckboxControl>
        <Text variant="c1" as="span" className="label-text">
          {label}
        </Text>
      </CheckboxLabel>
    </>
  );
});
